/**
 * Takeoff store — one provider per Estimating 2 tab. Owns the workspace
 * payload (projects/folders/library), the active project, its saved takeoff
 * document (measurements, page state, labels, estimate data), the job item
 * palette and drawing list. Doc edits autosave with a ~700 ms debounce and
 * surface Saving… / Saved / Cloud error without interrupting work.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as T from "./api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
export const useTakeoff = () => useContext(Ctx);

export const emptyDoc = () => ({
  items: [], pages: {}, jobLabels: [], manualEstimateItems: [],
  supportPriceOverrides: {}, pvcPriceOverrides: {}, ductPriceOverrides: {},
  estimateDescriptions: {}, estimatingNotes: {},
});

// Recent sheets are per user AND per job — two estimators sharing a
// workstation never see each other's sheets.
const recentKey = (uid, pid) => `takeoff-recent-${uid || "anon"}-${pid}`;
const legacyRecentKey = (pid) => `takeoff-recent-${pid}`;
export const getRecents = (uid, pid) => {
  if (!pid) return [];
  try {
    const cur = JSON.parse(localStorage.getItem(recentKey(uid, pid)) || "null");
    if (cur) return cur;
    // one-time migration from the pre-per-user key
    const legacy = JSON.parse(localStorage.getItem(legacyRecentKey(pid)) || "null");
    if (legacy) {
      localStorage.setItem(recentKey(uid, pid), JSON.stringify(legacy));
      localStorage.removeItem(legacyRecentKey(pid));
      return legacy;
    }
    return [];
  } catch { return []; }
};

export function TakeoffProvider({ children, design = false }) {
  const { user } = useAuth() || {};
  const userKey = user?.user_id || user?.id || user?.email || "anon";
  const [workspace, setWorkspace] = useState(null);
  const [wsError, setWsError] = useState(null);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem("takeoff-active-project") || "");
  const [doc, setDoc] = useState(null);
  const [jobItems, setJobItems] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saving | saved | error
  const docRef = useRef(null);
  const saveTimer = useRef(null);
  const undoStack = useRef([]);
  const [undoLen, setUndoLen] = useState(0);

  const wsRetryCount = useRef(0);
  const loadWorkspace = useCallback(async () => {
    try {
      setWsError(null);
      setWorkspace(await T.loadWorkspace(design));
      wsRetryCount.current = 0;
    } catch (e) {
      setWsError(e?.response?.data?.detail || "Could not load estimating workspace");
      // Transient network/SW hiccups (e.g. the PWA regaining focus) shouldn't
      // leave the module dead — auto-retry a couple of times before giving up.
      if (wsRetryCount.current < 2) {
        wsRetryCount.current += 1;
        setTimeout(() => { loadWorkspace(); }, 2500);
      }
    }
  }, [design]);
  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const activeProject = useMemo(
    () => (workspace?.projects || []).find((p) => p.project_id === activeId) || null,
    [workspace, activeId]
  );

  const setActiveId = useCallback((id) => {
    setActiveIdState(id || "");
    try {
      if (id) localStorage.setItem("takeoff-active-project", id);
      else localStorage.removeItem("takeoff-active-project");
    } catch { /* ignore */ }
  }, []);

  // ---- per-project document load ----
  useEffect(() => {
    if (!activeId) { docRef.current = null; setDoc(null); setJobItems([]); setDrawings([]); return undefined; }
    let alive = true;
    undoStack.current = [];
    setUndoLen(0);
    setProjectLoading(true);
    (async () => {
      try {
        const [st, items, drs] = await Promise.all([
          T.loadState(activeId), T.listItems(activeId), T.listDrawings(activeId),
        ]);
        if (!alive) return;
        const data = { ...emptyDoc(), ...(st.data || {}) };
        docRef.current = data;
        setDoc(data);
        setJobItems(items || []);
        setDrawings(drs || []);
        setSaveState("saved");
      } catch {
        if (alive) setSaveState("error");
      } finally {
        if (alive) setProjectLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [activeId]);

  // ---- autosave ----
  const flush = useCallback(async () => {
    if (!activeId || !docRef.current) return;
    setSaveState("saving");
    try {
      await T.saveState(activeId, docRef.current);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [activeId]);

  const scheduleSave = useCallback(() => {
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 700);
  }, [flush]);

  /** Mutate the active project's document. fn receives a draft clone.
   * Drives off docRef (kept in sync on load/undo) so the previous snapshot
   * can be pushed onto the undo stack exactly once per edit.
   * NO-OP for viewers — they can pan/zoom/browse but never edit. */
  const viewerRef = useRef(false);
  useEffect(() => { viewerRef.current = !!workspace?.viewer || ["manager", "viewer"].includes(user?.role); }, [workspace, user]);
  const mutate = useCallback((fn) => {
    if (viewerRef.current) return;
    const prev = docRef.current;
    if (!prev) return;
    const draft = structuredClone(prev);
    fn(draft);
    undoStack.current.push(prev);
    if (undoStack.current.length > 25) undoStack.current.shift();
    setUndoLen(undoStack.current.length);
    docRef.current = draft;
    setDoc(draft);
    scheduleSave();
  }, [scheduleSave]);

  /** Restore the document snapshot from before the most recent edit. */
  const undo = useCallback(() => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    setUndoLen(undoStack.current.length);
    docRef.current = prev;
    setDoc(prev);
    scheduleSave();
  }, [scheduleSave]);

  // flush on tab hide so little work is ever unsaved
  useEffect(() => {
    const onVis = () => { if (document.hidden) flush(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [flush]);

  // ---- project CRUD ----
  const createProject = useCallback(async (p) => { const r = await T.createProject(design ? { ...p, design: true } : p); await loadWorkspace(); return r; }, [loadWorkspace, design]);
  const updateProject = useCallback(async (id, p) => {
    await T.updateProject(id, p);
    setWorkspace((w) => w && { ...w, projects: w.projects.map((x) => (x.project_id === id ? { ...x, ...(p.name ? { name: p.name } : {}), ...(p.status ? { status: p.status } : {}), ...("dueDate" in p ? { due_date: p.dueDate } : {}), ...("notes" in p ? { notes: p.notes } : {}) } : x)) });
  }, []);
  const deleteProject = useCallback(async (id) => {
    await T.deleteProject(id);
    if (activeId === id) setActiveId("");
    await loadWorkspace();
  }, [activeId, loadWorkspace, setActiveId]);

  // ---- folders ----
  const createFolder = useCallback(async (pid, name, opts = {}) => { const r = await T.createFolder(pid, name, opts); await loadWorkspace(); return r; }, [loadWorkspace]);
  const renameFolder = useCallback(async (id, name) => {
    await T.renameFolder(id, name);
    setWorkspace((w) => w && { ...w, folders: w.folders.map((f) => (f.folder_id === id ? { ...f, name } : f)) });
  }, []);
  const moveFolder = useCallback(async (id, parentId) => {
    await T.moveFolder(id, parentId);
    setWorkspace((w) => w && { ...w, folders: w.folders.map((f) => (f.folder_id === id ? { ...f, parent_id: parentId || null } : f)) });
  }, []);
  const moveDrawings = useCallback(async (ids, folderId, category = null) => {
    await T.moveDrawings(ids, folderId, category);
    setDrawings((ds) => ds.map((d) => (ids.includes(d.drawing_id)
      ? { ...d, folder_id: folderId || null, ...(category ? { category } : {}) }
      : d)));
  }, []);
  const deleteFolder = useCallback(async (id) => {
    await T.deleteFolder(id);
    setDrawings((ds) => ds.map((d) => (d.folder_id === id ? { ...d, folder_id: null } : d)));
    await loadWorkspace();
  }, [loadWorkspace]);

  // ---- drawings ----
  const refreshDrawings = useCallback(async () => {
    if (!activeId) return;
    setDrawings(await T.listDrawings(activeId));
  }, [activeId]);
  /** Background optimization watcher: polls one drawing until the server
   * clears `optimizing`, then refreshes the list (PDF → TIFF swaps change
   * the mime the canvas fetches with). Fire-and-forget; never blocks uploads. */
  const watchOptimizing = useCallback((drawingId, name) => {
    (async () => {
      const t0 = Date.now();
      let d = null;
      while (Date.now() - t0 < 900_000) {
        await new Promise((r) => setTimeout(r, 4000));
        d = await T.getDrawing(drawingId).catch(() => null);
        if (!d || !d.optimizing) break;
      }
      if (d?.optimize_error) {
        const { toast } = await import("sonner");
        toast.warning(`${name}: optimization failed (${d.optimize_error}) — the original file was kept`);
      }
      refreshDrawings();
    })();
  }, [refreshDrawings]);
  const patchDrawing = useCallback(async (id, patch) => {
    await T.updateDrawing(id, patch);
    setDrawings((ds) => ds.map((d) => (d.drawing_id === id ? { ...d, ...("folderId" in patch ? { folder_id: patch.folderId } : {}), ...(patch.category ? { category: patch.category } : {}), ...(patch.name ? { name: patch.name } : {}) } : d)));
  }, []);
  const removeDrawing = useCallback(async (id) => {
    await T.deleteDrawing(id);
    setDrawings((ds) => ds.filter((d) => d.drawing_id !== id));
  }, []);

  // ---- job palette items ----
  const addJobItem = useCallback(async (item) => {
    const r = await T.addItem(activeId, item);
    setJobItems((p) => [...p, r]);
    return r;
  }, [activeId]);
  const updateJobItem = useCallback(async (id, patch) => {
    await T.updateItem(id, patch);
    setJobItems((p) => p.map((i) => (i.item_id === id ? { ...i, ...patch } : i)));
    // editing a job pipe/duct updates measured takeoffs that reference it
    if (patch.name || patch.rate != null || patch.color || patch.ductSpec || patch.priceTo != null) {
      mutate((d) => {
        for (const it of d.items) {
          if (it.tagId !== id) continue;
          if (patch.name) it.name = patch.name;
          if (patch.rate != null) it.rate = patch.rate;
          if (patch.color) it.color = patch.color;
          if (patch.ductSpec) it.ductSpec = patch.ductSpec;
          if (patch.priceTo != null) it.priceTo = patch.priceTo;
        }
      });
    }
  }, [mutate]);
  const removeJobItem = useCallback(async (id) => {
    await T.deleteItem(id); // measured geometry deliberately survives
    setJobItems((p) => p.filter((i) => i.item_id !== id));
  }, []);

  // ---- company library ----
  const addLibraryItem = useCallback(async (item) => { const r = await T.addLibraryItem(item); await loadWorkspace(); return r; }, [loadWorkspace]);
  const updateLibraryItem = useCallback(async (id, patch) => {
    await T.updateLibraryItem(id, patch);
    setWorkspace((w) => w && { ...w, library: w.library.map((i) => (i.item_id === id ? { ...i, ...patch } : i)) });
  }, []);
  const removeLibraryItem = useCallback(async (id) => {
    await T.deleteLibraryItem(id);
    setWorkspace((w) => w && { ...w, library: w.library.filter((i) => i.item_id !== id) });
    setJobItems((p) => p.filter((i) => i.library_id !== id));
  }, []);

  // ---- recents (per-user, per-job local device convenience state) ----
  const [recents, setRecents] = useState(() => getRecents(userKey, localStorage.getItem("takeoff-active-project") || ""));
  useEffect(() => { setRecents(getRecents(userKey, activeId)); }, [activeId, userKey]);
  const pushRecent = useCallback((entry) => {
    if (!activeId) return;
    const list = [entry, ...getRecents(userKey, activeId).filter((r) => !(r.fileId === entry.fileId && r.page === entry.page))].slice(0, 5);
    try { localStorage.setItem(recentKey(userKey, activeId), JSON.stringify(list)); } catch { /* ignore */ }
    setRecents(list);
  }, [activeId, userKey]);

  // ---- uploads (header progress + dismissible errors) ----
  // each entry: {key, name, pct, status: uploading|processing|ready, error,
  //              errorMeta, uploadId, file, path, info:{speedBps,etaText,chunk,retries}}
  // Failed entries persist to localStorage so a page refresh never loses a
  // resumable upload — the server keeps confirmed chunks; the user re-picks
  // the same file and the transfer continues from the last confirmed range.
  const FAILED_KEY = "takeoff-failed-uploads";
  const [uploads, setUploads] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FAILED_KEY) || "[]");
      return saved.map((s) => ({ ...s, file: null, pct: s.pct || 0, info: {} }));
    } catch { return []; }
  });
  const persistFailed = useCallback((list) => {
    try {
      localStorage.setItem(FAILED_KEY, JSON.stringify(
        list.filter((x) => x.error).map(({ key, name, error, errorMeta, uploadId, path, pct }) =>
          ({ key, name, error, errorMeta, uploadId, path, pct }))));
    } catch { /* storage full/blocked — resume just won't survive refresh */ }
  }, []);
  const beginUpload = useCallback((key, name, file, path) => setUploads((u) => [...u, { key, name, pct: 0, status: "uploading", file, path, info: {} }]), []);
  const progressUpload = useCallback((key, info) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct: info.pct ?? x.pct, status: "uploading", info } : x))), []);
  const statusUpload = useCallback((key, status, extra = {}) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, status, ...extra } : x))), []);
  const endUpload = useCallback((key, error, errorMeta) =>
    setUploads((u) => {
      const next = error ? u.map((x) => (x.key === key ? { ...x, error, errorMeta, status: "error" } : x)) : u.filter((x) => x.key !== key);
      persistFailed(next);
      return next;
    }), [persistFailed]);
  const dismissUpload = useCallback((key) => setUploads((u) => {
    const next = u.filter((x) => x.key !== key);
    persistFailed(next);
    return next;
  }), [persistFailed]);

  // batch tracker — "12 of 46 done" in the header while a multi-file
  // upload runs; cleared when the batch settles
  const [uploadBatch, setUploadBatch] = useState(null);

  /** Upload one or more files into the active job (used by header, plans
   * panel and drag-drop alike). Files transfer concurrently (max 3 active
   * transfers); ZIPs unpack server-side as a background job we poll. A
   * failed upload keeps its server session — Retry resumes from the last
   * confirmed byte range. */
  const pauseControllers = useRef({}); // key → AbortController for Pause
  const uploadFiles = useCallback(async (files, { folderId = null, category = null } = {}) => {
    if (!activeId || viewerRef.current) return; // viewers can't upload
    const list = Array.from(files || []).map((f) => (f instanceof File ? { file: f, path: f.webkitRelativePath || "" } : f));
    setUploadBatch({ total: list.length, done: 0 });
    const runOne = async ({ file: f, path, uploadId: resumeId, key: resumeKey }) => {
      const key = resumeKey || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${f.name}`;
      if (!resumeKey) beginUpload(key, f.name, f, path);
      else statusUpload(key, "uploading", { error: null, errorMeta: null });
      // Pause: one controller per transfer; Pause aborts the in-flight parts
      // and the pump stops with a PausedError (server session kept).
      const pauser = new AbortController();
      pauseControllers.current[key] = pauser;
      try {
        const res = await T.uploadDrawing(activeId, f, {
          folderId, path, uploadId: resumeId || undefined, ...(category ? { category } : {}),
          onProgress: (info) => progressUpload(key, info),
          pauseSignal: pauser.signal,
        });
        statusUpload(key, "processing", { pct: 100 });
        if (res?.zip) {
          // extraction runs in a background job — poll until done
          const t0 = Date.now();
          let st = null;
          while (Date.now() - t0 < 120_000) {
            await new Promise((r) => setTimeout(r, 1200));
            st = await T.uploadStatus(res.upload_id).catch(() => null);
            if (st && st.state !== "processing") break;
          }
          if (!st || st.state === "error") {
            const err = new Error(st?.error || "ZIP extraction failed");
            err.upload = { correlationId: res.upload_id, stage: "Extracting ZIP", name: f.name, size: f.size, httpStatus: 500, lastStep: "Upload complete and processing" };
            throw err;
          }
          if (st.state !== "done") throw new Error("ZIP extraction is taking too long — check the job in a moment.");
          const r = st.result || {};
          const { toast } = await import("sonner");
          const bits = [`${r.created} of ${r.expected ?? r.created} file(s) added`];
          if (r.duplicates) bits.push(`${r.duplicates} already in the job (kept as-is)`);
          if (r.skipped) bits.push(`${r.skipped} unsupported type skipped`);
          if (r.blocked) bits.push(`${r.blocked} blocked as unsafe`);
          if (r.failed) {
            bits.push(`${r.failed} FAILED`);
            const names = (r.failed_entries || []).map((e) => e.path.split("/").pop()).slice(0, 5).join(", ");
            toast.error(`${r.failed} file(s) failed to import${names ? `: ${names}` : ""}. Upload the same ZIP again to retry — files already added are kept, nothing is duplicated.`, { duration: 15000 });
          }
          (r.failed ? toast.warning : toast.success)(`ZIP unpacked — ${bits.join(" · ")}`, { duration: r.failed ? 12000 : 6000 });
        } else if (res?.drawing_id && res.optimizing) {
          // dpi400/tiff optimization swaps the file in the background. The
          // plan is usable right away (Oct-27: no more waiting here); the
          // row carries an "Optimizing…" badge and the watcher below
          // refreshes the list the moment the swap lands.
          watchOptimizing(res.drawing_id, f.name);
        }
        // Progressive availability: this file lands in Plans the moment IT
        // is ready — no waiting for the rest of the batch. The row lingers
        // as "ready" for a few seconds so the user sees what completed.
        statusUpload(key, "ready", { pct: 100 });
        refreshDrawings();
        setTimeout(() => dismissUpload(key), 6000);
      } catch (e) {
        const meta = e.upload || {};
        if (e.paused) {
          // keep the file handle + server session; Resume picks up here
          statusUpload(key, "paused", { uploadId: meta.correlationId || resumeId || null, pct: meta.total ? Math.round((meta.done / meta.total) * 100) : undefined });
          return;
        }
        endUpload(key, e?.response?.data?.detail || e.message || `Could not upload ${f.name}`, {
          title: "File upload", stage: meta.stage || "upload", detail: e?.response?.data?.detail || e.message,
          correlationId: meta.correlationId || resumeId || null, name: f.name, size: f.size,
          received: meta.done, offset: meta.offset, chunk: meta.chunk,
          httpStatus: meta.httpStatus, retries: meta.retries, lastStep: "chunk upload",
          uploadId: meta.correlationId || resumeId || null,
        });
        if (meta.correlationId) statusUpload(key, "error", { uploadId: meta.correlationId });
      } finally {
        delete pauseControllers.current[key];
        setUploadBatch((b) => (b ? { ...b, done: b.done + 1 } : b));
      }
    };
    // at most 4 active transfers; navigation/tools stay responsive
    const queue = [...list];
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) await runOne(queue.shift());
    }));
    setUploadBatch(null);
    await refreshDrawings();
    await loadWorkspace();
  }, [activeId, beginUpload, progressUpload, statusUpload, endUpload, dismissUpload, refreshDrawings, loadWorkspace, watchOptimizing]);

  /** Resume a failed upload from its kept (or re-picked) file handle +
   * server session — confirmed chunks are NOT resent. Entries restored
   * after a page refresh have no file handle (browser rule), so the caller
   * passes a freshly picked File with the same content. */
  /** Pause a transfer: in-flight parts abort, confirmed parts stay on the
   * server, the row shows "Paused" with Resume. */
  const pauseUpload = useCallback((key) => { pauseControllers.current[key]?.abort(); }, []);

  const retryUpload = useCallback(async (key, pickedFile = null) => {
    const entry = uploads.find((u) => u.key === key);
    const file = pickedFile || entry?.file;
    if (!entry || !file) return;
    await uploadFiles([{ file, path: entry.path || "", uploadId: entry.uploadId || entry.errorMeta?.uploadId || null, key }]);
  }, [uploads, uploadFiles]);

  /** Persist the sheet this user is viewing (server-backed, per user). */
  const lastSheetSent = useRef("");
  const saveLastSheet = useCallback((fileId, page) => {
    if (!activeId || !fileId) return;
    const k = `${activeId}:${fileId}:${page}`;
    if (lastSheetSent.current === k) return;
    lastSheetSent.current = k;
    T.setLastSheet(activeId, fileId, page).catch(() => {});
  }, [activeId]);

  const value = {
    workspace, wsError, loadWorkspace, design,
    // Viewer mode (manager role): server strips all pricing and 403s every
    // write; the UI mirrors it by disabling edits and hiding price surfaces.
    viewer: !!workspace?.viewer || ["manager", "viewer"].includes(user?.role),
    statuses: workspace?.statuses || [],
    library: workspace?.library || [],
    folders: (workspace?.folders || []).filter((f) => f.project_id === activeId),
    timeDaily: workspace?.time_daily || [],
    activeId, setActiveId, activeProject, projectLoading,
    doc, mutate, saveState, flush,
    undo, canUndo: undoLen > 0,
    saveLastSheet,
    retryUpload, pauseUpload,
    jobItems, addJobItem, updateJobItem, removeJobItem,
    drawings, refreshDrawings, patchDrawing, removeDrawing,
    addLibraryItem, updateLibraryItem, removeLibraryItem,
    createProject, updateProject, deleteProject,
    createFolder, renameFolder, moveFolder, deleteFolder, moveDrawings,
    recents, pushRecent,
    uploads, uploadFiles, dismissUpload, uploadBatch,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
